// ============================================================
// ASCII MAPS - the fastest way to get a battlefield out of your head.
//
// Typing a map is faster than clicking one, and it is the format GMs
// already sketch in when they scribble a fight on paper. A legend maps
// characters to brushes; everything else is open ground.
//
//   ####################
//   #....o........o....#
//   #..................#
//   #....~~~~..[[[[....#
//   #..................#
//   ####################
//
// The result is an ordinary terrain record: nothing downstream can tell
// a typed map from a painted one.
//
// Pure. Zero React dependency.
// ============================================================
import { makeBoundary } from './boundary';
import type { Terrain } from './presets';
import { emptyGrid, setCells, cellKey, DEFAULT_MATERIALS } from './voxels';
import type { VoxelGrid } from './voxels';
import type { Zone } from './zones';
import { makeZone, zoneCellKey, ZONE_PRESETS } from './zones';

/**
 * Character → what to build there.
 *
 * A `height` in cells builds a column of that material; a `zone` paints
 * ground instead. Chosen to look like the thing they draw.
 */
export interface AsciiEntry {
  material?: string;
  /** Cells tall. */
  height?: number;
  /** A ZONE_PRESETS key, for ground rather than volume. */
  zone?: string;
}

export const ASCII_LEGEND: Record<string, AsciiEntry> = {
  '#': { material: 'stone', height: 3 }, // wall
  '+': { material: 'steel', height: 3 }, // bulkhead
  o: { material: 'wood', height: 1 }, // crate
  '=': { material: 'stone', height: 1 }, // barricade
  I: { material: 'stone', height: 4 }, // pillar
  '^': { material: 'rubble', height: 2 }, // spoil heap
  X: { material: 'steel', height: 2 }, // platform
  '"': { material: 'mesh', height: 1 }, // fence, see-through
  g: { material: 'glass', height: 2 }, // window wall
  ':': { zone: 'difficult' }, // rough going
  '~': { zone: 'hazard' }, // burning, toxic
  '[': { zone: 'void' }, // impassable
};

export interface AsciiOptions {
  /** Metres per character cell. */
  tileSize?: number;
  /** Extra characters, or overrides of the defaults. */
  legend?: Record<string, AsciiEntry>;
  name?: string;
}

export interface AsciiResult {
  terrain: Terrain;
  /** Characters that meant nothing, so the GM can fix their legend. */
  unknown: string[];
}

/**
 * Parse a block of text into terrain.
 *
 * The grid is centred on the origin, so a map typed anywhere lands where
 * the camera already looks. Rows are padded to the longest line rather
 * than rejected: a hand-typed map has ragged right edges, and refusing
 * one over trailing whitespace would be obnoxious.
 */
export function parseAsciiMap(text: string, options: AsciiOptions = {}): AsciiResult {
  const tile = options.tileSize && options.tileSize > 0 ? options.tileSize : 1;
  const legend = { ...ASCII_LEGEND, ...(options.legend || {}) };
  const typed = readCells(trimmedRows(text), legend);
  // Built in this order on purpose: makeZone mints ids from a counter.
  const voxels = voxelsFrom(typed.byMaterial, tile);
  const zones = zonesFrom(typed.byZone);
  const boundary = fittedBoundary(typed.width, typed.height, tile);
  return {
    terrain: { boundary, solids: [], voxels, zones, name: options.name || 'Typed map' },
    unknown: [...typed.unknown],
  };
}

/**
 * The typed lines, blank lines dropped at the ends only - a blank line
 * in the MIDDLE is a real row of open ground.
 */
function trimmedRows(text: string): string[] {
  const rows = text.replace(/\r/g, '').split('\n');
  while (rows.length && !rows[0].trim()) rows.shift();
  while (rows.length && !rows[rows.length - 1].trim()) rows.pop();
  return rows;
}

/** Every typed character, sorted into what it builds. */
interface TypedCells {
  width: number;
  height: number;
  unknown: Set<string>;
  /** Material → voxel keys. */
  byMaterial: Map<string, string[]>;
  /** Zone preset → zone cell keys. */
  byZone: Map<string, string[]>;
}

/**
 * Read the grid. Cell indices are centred on the origin so a typed map
 * lands where the camera already looks.
 */
function readCells(rows: string[], legend: Record<string, AsciiEntry>): TypedCells {
  const width = rows.reduce((n, r) => Math.max(n, r.length), 0);
  const height = rows.length;
  const typed: TypedCells = { width, height, unknown: new Set(), byMaterial: new Map(), byZone: new Map() };
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const x = Math.round(col - (width - 1) / 2);
      const z = Math.round(row - (height - 1) / 2);
      placeChar(typed, legend[rows[row][col]], rows[row][col], { x, z });
    }
  }
  return typed;
}

/** One character: open ground, something the legend does not know, a zone, or a column of voxels. */
function placeChar(
  typed: TypedCells,
  entry: AsciiEntry | undefined,
  ch: string | undefined,
  { x, z }: { x: number; z: number },
): void {
  if (!ch || ch === ' ' || ch === '.') return;
  if (!entry) {
    typed.unknown.add(ch);
    return;
  }
  if (entry.zone) {
    append(typed.byZone, entry.zone, [zoneCellKey(x, z)]);
    return;
  }
  const column = Array.from({ length: Math.max(1, entry.height ?? 1) }, (_, y) => cellKey(x, y, z));
  append(typed.byMaterial, entry.material || 'stone', column);
}

/** Add keys to a map of lists, starting the list if it is new. */
function append(map: Map<string, string[]>, key: string, keys: string[]): void {
  const list = map.get(key) || [];
  list.push(...keys);
  map.set(key, list);
}

/** The voxel grid, a material the palette does not know built as stone. */
function voxelsFrom(byMaterial: Map<string, string[]>, tile: number): VoxelGrid {
  let voxels = emptyGrid(tile);
  for (const [material, keys] of byMaterial) {
    voxels = setCells(voxels, keys, material in DEFAULT_MATERIALS ? material : 'stone');
  }
  return voxels;
}

/** One zone per preset typed, an unknown preset built as the first. */
function zonesFrom(byZone: Map<string, string[]>): Zone[] {
  return [...byZone.entries()].map(([key, cells]) => {
    const preset = ZONE_PRESETS.find((p) => p.key === key) || ZONE_PRESETS[0];
    return makeZone({
      cells,
      going: preset.going,
      hazard: preset.hazard,
      colour: preset.colour,
      label: preset.label,
    });
  });
}

/**
 * Fit the arena to what was actually typed, in WORLD units. A cell
 * spans from its index to index+1, so a grid centred on cell indices is
 * half a cell off-centre in the world - which used to leave the
 * outermost column standing outside the boundary.
 */
function fittedBoundary(width: number, height: number, tile: number) {
  const half = (n: number) => (n * tile) / 2;
  const spanX = width || 1;
  const spanZ = height || 1;
  const minCellX = -Math.floor((width - 1) / 2);
  const minCellZ = -Math.floor((height - 1) / 2);
  const radius = Math.max(half(spanX), half(spanZ));
  return makeBoundary({
    sides: 4,
    radius,
    scaleX: half(spanX) / radius,
    scaleZ: half(spanZ) / radius,
    centre: { x: (minCellX + spanX / 2) * tile, z: (minCellZ + spanZ / 2) * tile },
    tileSize: tile,
  });
}

/** The legend, formatted for the import dialog. */
export function legendHelp(legend: Record<string, AsciiEntry> = ASCII_LEGEND): string {
  return Object.entries(legend)
    .map(([ch, entry]) => {
      if (entry.zone) return `${ch} ${ZONE_PRESETS.find((p) => p.key === entry.zone)?.label || entry.zone}`;
      const name = DEFAULT_MATERIALS[entry.material || '']?.name || entry.material;
      return `${ch} ${name}${entry.height && entry.height > 1 ? ` ×${entry.height}` : ''}`;
    })
    .join('   ');
}
